/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
function _createMdxContent(props) {
  const _components = Object.assign({
    h2: "h2",
    p: "p",
    code: "code",
    pre: "pre",
    h3: "h3",
    h4: "h4"
  }, _provideComponents(), props.components);
  return _jsxs(_Fragment, {
    children: [_jsx(_components.h2, {
      children: "Theming"
    }), "\n", _jsxs(_components.p, {
      children: ["styled-components has full theming support by exporting a ", _jsx(_components.code, {
        children: "<ThemeProvider>"
      }), " wrapper component.\nThis component provides a theme to all React components underneath itself via the context API. In the render\ntree all styled-components will have access to the provided theme, even when they are multiple levels deep."]
    }), "\n", _jsx(_components.p, {
      children: "To illustrate this, let's create our Button component, but this time we'll pass some variables down\nas a theme."
    }), "\n", _jsx(_components.pre, {
      children: _jsx(_components.code, {
        className: "language-react",
        children: "// Define our button, but with the use of props.theme this time\nconst Button = styled.button`\n  font-size: 1em;\n  margin: 1em;\n  padding: 0.25em 1em;\n  border-radius: 3px;\n\n  /* Color the border and text with theme.main */\n  color: ${props => props.theme.main};\n  border: 2px solid ${props => props.theme.main};\n`;\n\n// We are passing a default theme for Buttons that arent wrapped in the ThemeProvider\nButton.defaultProps = {\n  theme: {\n    main: \"#BF4F74\"\n  }\n}\n\n// Define what props.theme will look like\nconst theme = {\n  main: \"mediumseagreen\"\n};\n\nrender(\n  <div>\n    <Button>Normal</Button>\n\n    <ThemeProvider theme={theme}>\n      <Button>Themed</Button>\n    </ThemeProvider>\n  </div>\n);\n"
      })
    }), "\n", _jsx(_components.h3, {
      children: "Function themes"
    }), "\n", _jsxs(_components.p, {
      children: ["You can also pass a function for the theme prop. This function will receive the parent theme, that is from\nanother ", _jsx(_components.code, {
        children: "<ThemeProvider>"
      }), " higher up the tree. This way themes themselves can be made contextual."]
    }), "\n", _jsxs(_components.p, {
      children: ["This example renders our above themed Button and a second one that uses a second ThemeProvider to invert the\nbackground and foreground colors. The function ", _jsx(_components.code, {
        children: "invertTheme"
      }), " receives the upper theme and creates a new one."]
    }), "\n", _jsx(_components.pre, {
      children: _jsx(_components.code, {
        className: "language-react",
        children: "// Define our button, but with the use of props.theme this time\nconst Button = styled.button`\n  color: ${props => props.theme.fg};\n  border: 2px solid ${props => props.theme.fg};\n  background: ${props => props.theme.bg};\n\n  font-size: 1em;\n  margin: 1em;\n  padding: 0.25em 1em;\n  border-radius: 3px;\n`;\n\n// Define our `fg` and `bg` on the theme\nconst theme = {\n  fg: \"#BF4F74\",\n  bg: \"white\"\n};\n\n// This theme swaps `fg` and `bg`\nconst invertTheme = ({ fg, bg }) => ({\n  fg: bg,\n  bg: fg\n});\n\nrender(\n  <ThemeProvider theme={theme}>\n    <div>\n      <Button>Default Theme</Button>\n\n      <ThemeProvider theme={invertTheme}>\n        <Button>Inverted Theme</Button>\n      </ThemeProvider>\n    </div>\n  </ThemeProvider>\n);\n"
      })
    }), "\n", _jsx(_components.h3, {
      children: "Getting the theme without styled components"
    }), "\n", _jsxs(_components.h4, {
      children: ["via ", _jsx(_components.code, {
        children: "withTheme"
      }), " higher-order component"]
    }), "\n", _jsxs(_components.p, {
      children: ["If you ever need to use the current theme outside styled components (e.g. inside big components), you can use\nthe ", _jsx(_components.code, {
        children: "withTheme"
      }), " higher order component."]
    }), "\n", _jsx(_components.pre, {
      children: _jsx(_components.code, {
        className: "language-tsx",
        children: "import { withTheme } from 'styled-components'\n\nclass MyComponent extends React.Component {\n  render() {\n    console.log('Current theme: ', this.props.theme)\n    // ...\n  }\n}\n\nexport default withTheme(MyComponent)\n"
      })
    }), "\n", _jsxs(_components.h4, {
      children: ["via ", _jsx(_components.code, {
        children: "useContext"
      }), " React hook | v4"]
    }), "\n", _jsxs(_components.p, {
      children: ["You can also use ", _jsx(_components.code, {
        children: "useContext"
      }), " to access the current theme outside of styled components when working with React Hooks."]
    }), "\n", _jsx(_components.pre, {
      children: _jsx(_components.code, {
        className: "language-tsx",
        children: "import { useContext } from 'react'\nimport { ThemeContext } from 'styled-components'\n\nconst MyComponent = () => {\n  const themeContext = useContext(ThemeContext)\n\n  console.log('Current theme: ', themeContext)\n  // ...\n}\n"
      })
    }), "\n", _jsxs(_components.h4, {
      children: ["via ", _jsx(_components.code, {
        children: "useTheme"
      }), " custom hook | v5"]
    }), "\n", _jsxs(_components.p, {
      children: ["You can also use ", _jsx(_components.code, {
        children: "useTheme"
      }), " to access the current theme outside of styled components when working with React Hooks."]
    }), "\n", _jsx(_components.pre, {
      children: _jsx(_components.code, {
        className: "language-tsx",
        children: "import { useTheme } from 'styled-components'\n\nconst MyComponent = () => {\n  const theme = useTheme()\n\n  console.log('Current theme: ', theme)\n  // ...\n}\n"
      })
    }), "\n", _jsxs(_components.h3, {
      children: ["The ", _jsx(_components.code, {
        children: "theme"
      }), " prop"]
    }), "\n", _jsxs(_components.p, {
      children: ["A theme can also be passed down to a component using the ", _jsx(_components.code, {
        children: "theme"
      }), " prop."]
    }), "\n", _jsxs(_components.p, {
      children: ["This is useful to circumvent a missing ", _jsx(_components.code, {
        children: "ThemeProvider"
      }), " or to override it."]
    }), "\n", _jsx(_components.pre, {
      children: _jsx(_components.code, {
        className: "language-react",
        children: "// Define our button\nconst Button = styled.button`\n  font-size: 1em;\n  margin: 1em;\n  padding: 0.25em 1em;\n  border-radius: 3px;\n\n  /* Color the border and text with theme.main */\n  color: ${props => props.theme.main};\n  border: 2px solid ${props => props.theme.main};\n`;\n\n// Define what main theme will look like\nconst theme = {\n  main: \"mediumseagreen\"\n};\n\nrender(\n  <div>\n    <Button theme={{ main: \"royalblue\" }}>Ad hoc theme</Button>\n    <ThemeProvider theme={theme}>\n      <div>\n        <Button>Themed</Button>\n        <Button theme={{ main: \"darkorange\" }}>Overridden</Button>\n      </div>\n    </ThemeProvider>\n  </div>\n);\n"
      })
    })]
  });
}
function MDXContent(props = {}) {
  const {wrapper: MDXLayout} = Object.assign({}, _provideComponents(), props.components);
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, props)
  })) : _createMdxContent(props);
}
export default MDXContent;
